import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { type AmountUnit, DEFAULT_AMOUNT_UNIT } from '@/shared/lib/amount/convertAmount';

type AmountUnitState = {
  /** 現在の金額表示単位 */
  amountUnit: AmountUnit;
  /** 金額表示単位を設定 */
  setAmountUnit: (unit: AmountUnit) => void;
};

/**
 * 画面全体で金額表示単位（円/千円）を共有するストア
 *
 * タブ間・画面間で単位の選択状態を維持する。
 * sessionStorage に永続化されるため、リロード時も維持される（ブラウザタブを閉じるとリセット）。
 */
export const useAmountUnitStore = create<AmountUnitState>()(
  persist(
    (set) => ({
      amountUnit: DEFAULT_AMOUNT_UNIT,
      setAmountUnit: (unit) => set({ amountUnit: unit }),
    }),
    {
      name: 'amount-unit',
      storage: {
        getItem: (name) => {
          const value = sessionStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: (name, value) => sessionStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => sessionStorage.removeItem(name),
      },
    },
  ),
);

/**
 * 金額表示単位を取得し、ストア・URLに同期するフック
 *
 * 優先順位: URL > store > デフォルト(円)
 * - URLパラメータがあればそれを使用し、ストアに同期
 * - URLになければストアの値をフォールバックし、URLにも反映（URLコピペで再現可能にする）
 */
export const useAmountUnit = (urlAmountUnit?: AmountUnit): AmountUnit => {
  const navigate = useNavigate();
  const storeAmountUnit = useAmountUnitStore((state) => state.amountUnit);
  const setAmountUnit = useAmountUnitStore((state) => state.setAmountUnit);
  const amountUnit = urlAmountUnit ?? storeAmountUnit;

  // store に同期
  useEffect(() => {
    setAmountUnit(amountUnit);
  }, [amountUnit, setAmountUnit]);

  // URL にない場合、store の値を URL に反映（デフォルト値の場合は不要）
  useEffect(() => {
    if (!urlAmountUnit && storeAmountUnit !== DEFAULT_AMOUNT_UNIT) {
      navigate({
        to: '.',
        search: (prev) => ({ ...prev, amount_unit: storeAmountUnit }),
        replace: true,
      });
    }
  }, [urlAmountUnit, storeAmountUnit, navigate]);

  return amountUnit;
};
